import pino, {
  Bindings,
  ChildLoggerOptions,
  Logger as PinoLogger,
  LogFn,
} from 'pino';

// Map Pino levels to Google Cloud Logging severity levels
// https://cloud.google.com/logging/docs/reference/v2/rest/v2/LogEntry#LogSeverity
const levelToSeverity: any = {
  trace: 'DEBUG',
  debug: 'DEBUG',
  info: 'INFO',
  warn: 'WARNING',
  error: 'ERROR',
  fatal: 'CRITICAL',
};

// base this on NODE_ENV (set in our webpack configs)
const level = process.env.NODE_ENV === 'production' ? 'info' : 'trace';

/** Keep untrusted values on one physical line so they cannot forge log entries. */
export const sanitizeForLog = (value: unknown): string =>
  String(value)
    .replace(/\u2028|\u2029/g, '')
    .replace(/\n|\r/g, '');

export interface SanitizingLogger {
  trace: LogFn;
  debug: LogFn;
  info: LogFn;
  warn: LogFn;
  error: LogFn;
  fatal: LogFn;
  child(bindings: Bindings, options?: ChildLoggerOptions): SanitizingLogger;
}

const sanitizeLogArguments = (args: any[]): any[] =>
  args.map((arg) => (typeof arg === 'string' ? sanitizeForLog(arg) : arg));

const wrapLogMethod = (delegate: PinoLogger, method: LogFn): LogFn =>
  ((...args: any[]) => {
    (method as (...safeArgs: any[]) => void).apply(
      delegate,
      sanitizeLogArguments(args)
    );
  }) as LogFn;

/**
 * Keep the raw Pino object private: CodeQL models direct Pino calls as sinks,
 * while caller-supplied message strings pass through the sanitizer here.
 */
export const createSanitizingLogger = (
  delegate: PinoLogger,
  messageKey = 'message'
): SanitizingLogger => ({
  trace: wrapLogMethod(delegate, delegate.trace),
  debug: wrapLogMethod(delegate, delegate.debug),
  info: wrapLogMethod(delegate, delegate.info),
  warn: wrapLogMethod(delegate, delegate.warn),
  error: wrapLogMethod(delegate, delegate.error),
  fatal: wrapLogMethod(delegate, delegate.fatal),
  child(bindings, options) {
    const childBindings = bindings.serializers
      ? {
          ...bindings,
          serializers: {
            ...bindings.serializers,
            [messageKey]: sanitizeForLog,
          },
        }
      : bindings;
    const childOptions = {
      ...options,
      serializers: {
        ...options?.serializers,
        [messageKey]: sanitizeForLog,
      },
    };

    return createSanitizingLogger(
      delegate.child(childBindings, childOptions),
      messageKey
    );
  },
});

const pinoLogger = pino({
  level,
  name: 'digital-accounts-management',
  base: { serviceContext: { service: 'digital-accounts-management' } },
  formatters: {
    level(label: string) {
      const pinoLevel = label;
      const severity = levelToSeverity[pinoLevel];
      // `@type` property tells Error Reporting to track even if there is no `stack_trace`
      // you might want to make this an option the plugin, in our case we do want error reporting for all errors, with or without a stack
      const typeProp =
        pinoLevel === 'error' || pinoLevel === 'fatal'
          ? {
              '@type':
                'type.googleapis.com/google.devtools.clouderrorreporting.v1beta1.ReportedErrorEvent',
            }
          : {};
      return { severity, ...typeProp };
    },
    log(object: any) {
      const logObject = object as { err?: Error };
      const stackProp = logObject?.err?.stack
        ? { stack_trace: logObject.err.stack }
        : {};

      return {
        ...object,
        ...stackProp,
      };
    },
  },
  messageKey: 'message',
  serializers: { message: sanitizeForLog },
});

export const logger = createSanitizingLogger(pinoLogger);
